import { useEffect, useState } from "react"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonWarning } from "components/ui/buttons/ButtonWarning/ButtonWarning"
import { Modal } from "components/ui/modals/Modal/Modal"
import { H3 } from "components/ui/typography/titles/Titles"
import { Form, Input } from "antd"
import { InputProvider } from "components/providers/Input.provider"
import { useDeleteUser, useUsers } from "hooks/api/user/user.hook"
import { useMessage } from "hooks/message.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { isAxiosError } from "helpers/utils.helper"
import style from "./DeleteUserModal.module.scss"

const { buttonsBlock, title, warning, deleteButton } = style

type Props = {
	isModalOpen: boolean
	userData: { id: number; name: string }
	setDeleteUserModalClose: () => void
}

interface ErrorResponse extends Array<string> {}

export const DeleteUserModal = ({ isModalOpen, userData, setDeleteUserModalClose }: Props) => {
	const [form] = Form.useForm()
	const values = Form.useWatch(["password"], form)
	const [submittable, setSubmittable] = useState(false)
	const { mutate: deleteUserMutation, isPending: deleteUserPending } = useDeleteUser()
	const { success: successMessage, error: errorMessage } = useMessage()
	const { refetch: refetchUsers } = useUsers()

	// Validate password
	useEffect(() => {
		form.validateFields({ validateOnly: true }).then(
			() => {
				setSubmittable(true)
			},
			() => {
				setSubmittable(false)
			},
		)
	}, [form, values])

	// Delete user
	const deleteUserHandle = () => {
		deleteUserMutation(
			{ id: userData.id, password: values },
			{
				onSuccess: () => {
					setDeleteUserModalClose()
					successMessage(`${userData.name} successfully deleted!`)
					form.resetFields() // Reset form fields
					refetchUsers() // Refetch users
				},
				onError: (e) => {
					if (isAxiosError(e)) {
						const response = e.response
						if (response?.status === 400) {
							// Use type assertion to specify the type of response.data
							const errorData = response.data as ErrorResponse
							errorMessage(errorData[0])
						}
					}
				},
			},
		)
	}

	const onCloseModal = () => {
		setDeleteUserModalClose()
		form.resetFields()
	}

	return (
		<Modal isModalOpen={isModalOpen} setModalClose={setDeleteUserModalClose}>
			{deleteUserPending ? (
				<div data-testid="skeleton">
					<Skeleton />
				</div>
			) : (
				<>
					<H3 className={title}>Delete {userData.name}?</H3>
					<p className={warning}>Deleting this user will also permanently remove all their files.</p>
					<p className={warning}>If you’re sure, type your password in the box below to confirm.</p>
					<Form form={form} initialValues={{ password: "" }} size="large">
						<InputProvider>
							<Form.Item name="password" rules={[{ required: true }]}>
								<Input.Password placeholder="Password" />
							</Form.Item>
						</InputProvider>
						<div className={buttonsBlock}>
							<ButtonPrimary onClick={onCloseModal}>Cancel</ButtonPrimary>
							<ButtonWarning onClick={deleteUserHandle} submittable={submittable} className={deleteButton}>
								Delete
							</ButtonWarning>
						</div>
					</Form>
				</>
			)}
		</Modal>
	)
}
